import { getFreshAccessToken } from '../common/httpclient';

/**
 * Cliente do assistente de IA (`fv-ai`) — contrato em JORNADA_IA.md §2.4.
 *
 *  - POST   /ai/chat                 body {conversation_id?, message}; resposta SSE
 *                                    com os eventos `token`, `tool_status`, `done`, `error`.
 *  - GET    /ai/conversations        histórico do próprio usuário.
 *  - GET    /ai/conversations/{id}   conversa + mensagens.
 *  - DELETE /ai/conversations/{id}.
 *
 * Usa `fetch` (e não o axios do `httpclient`) porque o axios não expõe o corpo
 * como stream no navegador. O Bearer JWT vem do mesmo `access_token` do resto
 * do front (renovado por `getFreshAccessToken`). NUNCA se envia enterprise_id /
 * user_id: a identidade vem só do JWT, validado no `fv-ai`.
 */

// Gate simples da funcionalidade (build-time). O controle por empresa é o item 8.6.
export const isAiChatEnabled = () => process.env.REACT_APP_AI_ENABLED === 'true';

const baseUrl = () => (process.env.REACT_APP_AI_URL || '').replace(/\/+$/, '');

// Códigos de erro do `fv-ai` (fv-ai/chat/chat.go) mais os locais do front.
export const AI_ERROR = {
  COTA_EXCEDIDA: 'COTA_EXCEDIDA',
  LIMITE_DE_USO: 'LIMITE_DE_USO',
  NAO_AUTORIZADO: 'NAO_AUTORIZADO',
  SEM_PERMISSAO: 'SEM_PERMISSAO',
  CONVERSA_NAO_ENCONTRADA: 'CONVERSA_NAO_ENCONTRADA',
  REDE: 'REDE',
  DESCONHECIDO: 'ERRO_INTERNO',
};

export class AiChatError extends Error {
  constructor({ status = 0, codigo = AI_ERROR.DESCONHECIDO, mensagem = 'Não consegui responder agora.' } = {}) {
    super(mensagem);
    this.name = 'AiChatError';
    this.status = status;
    this.codigo = codigo;
  }
}

async function erroDaResposta(response) {
  let codigo = AI_ERROR.DESCONHECIDO;
  let mensagem = 'Não consegui responder agora.';
  try {
    const corpo = await response.json();
    if (corpo?.erro?.codigo) codigo = corpo.erro.codigo;
    if (corpo?.erro?.mensagem) mensagem = corpo.erro.mensagem;
  } catch (e) {
    // corpo não-JSON: mantém o genérico (nunca expõe o texto cru do servidor)
  }
  if (response.status === 403 && codigo === AI_ERROR.DESCONHECIDO) codigo = AI_ERROR.SEM_PERMISSAO;
  if (response.status === 401 && codigo === AI_ERROR.DESCONHECIDO) codigo = AI_ERROR.NAO_AUTORIZADO;
  return new AiChatError({ status: response.status, codigo, mensagem });
}

/**
 * fetch autenticado: um 401 dispara um único refresh forçado + nova tentativa.
 * Lança AiChatError em falha de rede/HTTP; AbortError é propagado como está.
 */
async function requisitar(path, init = {}) {
  const montar = async (force) => {
    const token = await getFreshAccessToken({ force });
    if (!token) {
      throw new AiChatError({ status: 401, codigo: AI_ERROR.NAO_AUTORIZADO, mensagem: 'Sessão expirada. Entre novamente.' });
    }
    return fetch(`${baseUrl()}${path}`, {
      ...init,
      headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` },
    });
  };

  let response;
  try {
    response = await montar(false);
    if (response.status === 401) response = await montar(true);
  } catch (e) {
    if (e?.name === 'AbortError' || e instanceof AiChatError) throw e;
    throw new AiChatError({ codigo: AI_ERROR.REDE, mensagem: 'Não consegui falar com o assistente. Verifique sua conexão.' });
  }
  if (!response.ok) throw await erroDaResposta(response);
  return response;
}

/**
 * Interpreta um bloco SSE ("event: x\ndata: {...}") e devolve {evento, dados}.
 * `data` multi-linha é unido com "\n"; JSON inválido vira null (evento ignorado).
 */
export function parseSseBlock(bloco) {
  let evento = 'message';
  const dados = [];
  bloco.split('\n').forEach((linha) => {
    const l = linha.replace(/\r$/, '');
    if (l.startsWith(':')) return; // comentário/keep-alive
    if (l.startsWith('event:')) evento = l.slice(6).trim();
    else if (l.startsWith('data:')) dados.push(l.slice(5).replace(/^ /, ''));
  });
  if (dados.length === 0) return null;
  try {
    return { evento, dados: JSON.parse(dados.join('\n')) };
  } catch (e) {
    return null;
  }
}

/**
 * Envia uma pergunta e consome o stream SSE.
 *
 * @param {object}   args
 * @param {string}   args.message
 * @param {string=}  args.conversationId
 * @param {AbortSignal=} args.signal       "parar" resposta (AbortController do chamador)
 * @param {(texto:string)=>void=}                  args.onToken
 * @param {({tool:string,mensagem:string})=>void=} args.onToolStatus
 * @param {(done:object)=>void=}                   args.onDone    {conversation_id, historico_salvo, uso}
 * @param {({codigo:string,mensagem:string})=>void=} args.onError  evento `error` (falha durante o stream)
 * @returns {Promise<{aborted:boolean}>} rejeita com AiChatError em falha HTTP (429, 401…) ou de rede.
 */
export async function sendChatMessage({ message, conversationId, signal, onToken, onToolStatus, onDone, onError }) {
  const body = { message };
  if (conversationId) body.conversation_id = conversationId;

  let response;
  try {
    response = await requisitar('/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (e?.name === 'AbortError') return { aborted: true };
    throw e;
  }

  const despachar = (bloco) => {
    const ev = parseSseBlock(bloco);
    if (!ev) return;
    if (ev.evento === 'token') onToken?.(String(ev.dados?.texto ?? ''));
    else if (ev.evento === 'tool_status') onToolStatus?.(ev.dados);
    else if (ev.evento === 'done') onDone?.(ev.dados);
    else if (ev.evento === 'error') onError?.(ev.dados);
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        despachar(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
        idx = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim()) despachar(buffer);
  } catch (e) {
    if (e?.name === 'AbortError') return { aborted: true };
    throw new AiChatError({ codigo: AI_ERROR.REDE, mensagem: 'A conexão com o assistente foi interrompida.' });
  }
  return { aborted: false };
}

export async function listConversations({ limit = 50, offset = 0, signal } = {}) {
  const response = await requisitar(`/ai/conversations?limit=${limit}&offset=${offset}`, { signal });
  const corpo = await response.json();
  return Array.isArray(corpo?.conversas) ? corpo.conversas : [];
}

export async function getConversation(id, { signal } = {}) {
  const response = await requisitar(`/ai/conversations/${encodeURIComponent(id)}`, { signal });
  const corpo = await response.json();
  return {
    conversa: corpo?.conversa || null,
    mensagens: Array.isArray(corpo?.mensagens) ? corpo.mensagens : [],
  };
}

export async function deleteConversation(id) {
  await requisitar(`/ai/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
